import { useEffect, useState } from 'react'
import { ROLES, isAdmin } from '../../shared/catalog.js'
import { ChatHeader } from '../components/ChatHeader.jsx'
import { useToast } from '../components/ui/Toast.jsx'
import { errorMessage } from '../lib/api.js'
import { formatRelative } from '../lib/dates.js'
import { useConnectMcp, useDisconnectMcp, useMcp } from '../lib/queries/mcp.js'
import { useMe, useSignOut } from '../lib/queries/session.js'
import { useSaveAiSettings, useSetUserRole, useSettings, useUsers } from '../lib/queries/settings.js'
import p from './pages.module.css'

export function SettingsPage() {
  const { user, workspace } = useMe()
  const signOut = useSignOut()
  const admin = isAdmin(user)

  return (
    <div className={p.chat}>
      <ChatHeader title="Settings" subtitle={admin ? 'Account, AI provider and members' : 'Your account'} />
      <div className={p.page}>
        <section className={p.panel}>
          <h2 className={p.panelTitle}>Account</h2>
          <div className={p.row}>
            {user?.picture ? <img className={p.avatar} src={user.picture} alt="" referrerPolicy="no-referrer" /> : null}
            <div>
              <div>{user?.name ?? user?.email}</div>
              <div className={p.muted}>
                {user?.email} · {user?.role}
              </div>
            </div>
          </div>
          <p className={p.panelLead} style={{ margin: 0 }}>
            Answers come from {workspace?.provider === 'anthropic' ? 'Anthropic' : workspace?.provider} · {workspace?.model}.
          </p>
          <div className={p.row}>
            <button type="button" className="btn btn-ghost" onClick={() => signOut()}>
              Sign out
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => signOut({ everywhere: true })}>
              Sign out everywhere
            </button>
          </div>
        </section>
        <McpPanel />
        {admin ? <AiSettingsPanel /> : null}
        {admin ? <MembersPanel currentUserId={user?.id} /> : null}
      </div>
    </div>
  )
}

function McpPanel() {
  const toast = useToast()
  const mcp = useMcp()
  const connect = useConnectMcp()
  const disconnect = useDisconnectMcp()
  const connected = Boolean(mcp.data?.connected)

  // Back from the Integrator MCP sign-in: report the result, drop ?mcp= from the address.
  useEffect(() => {
    const url = new URL(window.location.href)
    const result = url.searchParams.get('mcp')
    if (!result) return
    url.searchParams.delete('mcp')
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
    mcp.refetch()
    if (result === 'connected') toast.success('Connected to Integrator MCP. Answers can now use its live tools.')
    else if (result === 'cancelled') toast.error('Integrator MCP sign-in was cancelled.')
    else toast.error('Could not connect to Integrator MCP. Try again.')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <section className={p.panel}>
      <h2 className={p.panelTitle}>Integrator MCP</h2>
      <p className={p.panelLead}>
        {connected
          ? 'Connected. Answers can call Integrator’s live tools on your behalf.'
          : 'Sign in to the Integrator MCP server so answers can use its live tools, not only the skill files.'}
      </p>
      <div className={p.row}>
        {connected ? (
          <button
            type="button"
            className="btn btn-ghost"
            disabled={disconnect.isPending}
            onClick={() =>
              disconnect.mutate(undefined, {
                onSuccess: () => toast.success('Disconnected from Integrator MCP.'),
                onError: (error) => toast.error(errorMessage(error)),
              })
            }
          >
            Disconnect
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-dark"
            disabled={connect.isPending || mcp.isPending}
            onClick={() => connect.mutate(undefined, { onError: (error) => toast.error(errorMessage(error)) })}
          >
            {connect.isPending ? 'Opening sign-in…' : 'Connect to Integrator MCP'}
          </button>
        )}
      </div>
    </section>
  )
}

function AiSettingsPanel() {
  const settings = useSettings()
  const save = useSaveAiSettings()
  const [form, setForm] = useState(null)

  useEffect(() => {
    if (settings.data && !form) setForm(settings.data.ai)
  }, [settings.data, form])

  if (!settings.data || !form) {
    return (
      <section className={p.panel}>
        <div className="skeleton" style={{ height: 140 }} />
      </section>
    )
  }
  const providers = settings.data.providers
  const provider = providers.find((x) => x.key === form.provider)

  return (
    <section className={p.panel}>
      <h2 className={p.panelTitle}>AI provider</h2>
      <p className={p.panelLead}>
        Anthropic is the default. A provider needs its API key set as a Worker secret before it can answer.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          save.mutate(form)
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div className={p.formGrid}>
          <label className="field">
            <span>Provider</span>
            <select
              value={form.provider}
              onChange={(e) => {
                const next = providers.find((x) => x.key === e.target.value)
                setForm({ ...form, provider: e.target.value, model: next?.defaultModel ?? form.model })
              }}
            >
              {providers.map((x) => (
                <option key={x.key} value={x.key}>
                  {x.label}
                  {x.configured ? '' : ' (no API key)'}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Model</span>
            <input list="model-options" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
            <datalist id="model-options">
              {(provider?.suggestedModels ?? []).map((m) => (
                <option key={m} value={m} />
              ))}
            </datalist>
          </label>
          <label className="field">
            <span>Max output tokens</span>
            <input
              type="number"
              min={512}
              max={64000}
              step={256}
              value={form.maxTokens}
              onChange={(e) => setForm({ ...form, maxTokens: Number(e.target.value) })}
            />
          </label>
          <label className="field">
            <span>Skill context budget (characters)</span>
            <input
              type="number"
              min={20000}
              max={2000000}
              step={10000}
              value={form.contextBudgetChars}
              onChange={(e) => setForm({ ...form, contextBudgetChars: Number(e.target.value) })}
            />
            <small>Above this, only SKILL.md is sent and the model opens references with tools.</small>
          </label>
        </div>
        {provider && !provider.configured ? (
          <span className="field-error">{provider.label} has no API key configured — answers will fail until it is set.</span>
        ) : null}
        <div>
          <button type="submit" className="btn btn-dark" disabled={save.isPending}>
            Save
          </button>
        </div>
      </form>
    </section>
  )
}

function MembersPanel({ currentUserId }) {
  const users = useUsers()
  const setRole = useSetUserRole()
  return (
    <section className={p.panel}>
      <h2 className={p.panelTitle}>Members</h2>
      <p className={p.panelLead}>Editors can change skills; admins can also manage settings and roles.</p>
      {users.isPending ? (
        <div className="skeleton" style={{ height: 120 }} />
      ) : (
        <table className={p.userTable}>
          <thead>
            <tr>
              <th scope="col" className="section-label">
                Member
              </th>
              <th scope="col" className="section-label">
                Last sign-in
              </th>
              <th scope="col" className="section-label">
                Role
              </th>
            </tr>
          </thead>
          <tbody>
            {users.data.map((member) => (
              <tr key={member.id}>
                <td>
                  <div>{member.name ?? member.email}</div>
                  <div className={p.muted}>{member.email}</div>
                </td>
                <td className={p.muted}>{formatRelative(member.lastLoginAt)}</td>
                <td>
                  <select
                    value={member.role}
                    aria-label={`Role for ${member.email}`}
                    disabled={member.id === currentUserId}
                    onChange={(e) => setRole.mutate({ id: member.id, role: e.target.value })}
                  >
                    {ROLES.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
